import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import Dialog from "../../components/ui/Dialog";
import {
  useReplenishRuns,
  useRunReplenish,
} from "../../api/queries/leaveQueries";
import { getApiErrorMessage } from "../../utils/apiError";
import {
  formatDateTime,
  formatPlainDate,
  todayInManila,
} from "../../utils/dateTime";
import { downloadTextFile, toCsv } from "../../utils/csv";
import "./leave.css";
import { useCanModify } from "../../hooks/usePermissions";
import usePermissions from "../../hooks/usePermissions";

const SKIP_LABELS = {
  separated: "Separated",
  noHiringDate: "No hiring date",
  invalidHiringDate: "Invalid hiring date",
  notEligibleYet: "Not eligible yet",
};

/** HR downloads the employees whose hiring date can't be used, to fix them. */
const downloadInvalidHireDates = (run) =>
  downloadTextFile(
    toCsv(
      ["Employee no.", "Hiring date on file", "Employee id"],
      (run.details?.invalidHireDates || []).map((r) => [
        r.employeeNumber,
        r.hiringDate,
        r.employeeId,
      ]),
    ),
    `invalid_hiring_dates_${run.runDate || "run"}.csv`,
  );

/** Counts, skip reasons, invalid dates and errors of one run (or a dry run). */
const RunDetails = ({ run }) => {
  const d = run.details || {};
  const skipped = d.skipped || {};
  const invalid = d.invalidHireDates || [];
  const errors = d.errors || [];
  return (
    <div className="replenish-details">
      {d.toCreateByPolicy && (
        <div>
          <div className="approval-meta-label">Would create</div>
          <div className="d-flex flex-wrap gap-2">
            {Object.entries(d.toCreateByPolicy).map(([code, n]) => (
              <span key={code} className="status-pill status-pill--blue">
                {code}: {n.toLocaleString()}
              </span>
            ))}
          </div>
        </div>
      )}
      <div>
        <div className="approval-meta-label">Skipped</div>
        <ul className="replenish-skips">
          {Object.entries(skipped).map(([k, n]) => (
            <li key={k}>
              <span>{SKIP_LABELS[k] || k}</span>
              <strong className="tabular-nums">
                {Number(n).toLocaleString()}
              </strong>
            </li>
          ))}
          {Object.keys(skipped).length === 0 && (
            <li className="text-slate-500">None</li>
          )}
        </ul>
      </div>
      {invalid.length > 0 && (
        <div>
          <div className="approval-meta-label">
            Invalid hiring dates ({invalid.length})
          </div>
          <p className="fs-080 text-slate-600 mb-2">
            These employees can't get leave until their hiring date is
            corrected.
          </p>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => downloadInvalidHireDates(run)}
          >
            <i className="bi bi-download me-1"></i>Download list (CSV)
          </button>
        </div>
      )}
      {errors.length > 0 && (
        <div>
          <div className="approval-meta-label text-danger">
            Errors ({errors.length})
          </div>
          <ul className="fs-080 mb-0 ps-3">
            {errors.slice(0, 20).map((e, i) => (
              <li key={i}>
                {typeof e === "string" ? e : e.message || JSON.stringify(e)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(d.sample) && d.sample.length > 0 && (
        <div>
          <div className="approval-meta-label">Sample ({d.sample.length})</div>
          <pre className="replenish-sample">
            {JSON.stringify(d.sample.slice(0, 10), null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

/**
 * Leave replenish job: the daily run (01:30 Manila) creates each employee's
 * new anniversary cycle and closes the old one. HR can see past runs, dry-run
 * and run it manually for a date.
 */
const ReplenishRunsPage = () => {
  const canModify = useCanModify("leave", { company: true });
  // HR-level (leave with COMPANY scope); role names grant nothing
  const isCompanyWide = usePermissions().companyWide("leave");
  const runs = useReplenishRuns(20);
  const run = useRunReplenish();
  const today = todayInManila();

  const [expanded, setExpanded] = useState(null);
  const [showRun, setShowRun] = useState(false);
  const [date, setDate] = useState(today);
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const start = () => {
    setError(null);
    setResult(null);
    run.mutate(
      { date, dryRun },
      {
        onSuccess: (res) => {
          setResult({ ...res, dryRun });
          toast.success(
            dryRun
              ? "Dry run finished: nothing was changed."
              : `Run finished: ${res.created ?? 0} created, ${res.closed ?? 0} closed.`,
          );
        },
        onError: (err) =>
          setError(getApiErrorMessage(err, "Could not run the job.")),
      },
    );
  };

  if (!isCompanyWide) {
    return (
      <Layout>
        <div className="container-fluid py-4 px-lg-4">
          <div className="leave-card approval-empty">
            <i className="bi bi-shield-lock"></i>Only Admin and HR can view
            replenish runs.
          </div>
        </div>
      </Layout>
    );
  }

  const list = runs.data || [];

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <Link
              to="/leave/allocation"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Leave Allocation
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title">
              Leave Replenish Runs
            </h1>
            <p className="text-muted small mb-0">
              The job runs daily at 1:30 AM and creates each employee's new
              cycle on their hiring anniversary.
            </p>
          </div>
          {canModify && (
            <button
              type="button"
              className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
              onClick={() => {
                setShowRun(true);
                setResult(null);
                setError(null);
                setDryRun(true);
                setDate(today);
              }}
            >
              <i className="bi bi-play-fill me-2"></i> Run now
            </button>
          )}
        </div>

        <div className="glass-table-card">
          {runs.isError ? (
            <div className="approval-empty text-danger">
              <i className="bi bi-exclamation-triangle"></i>
              {getApiErrorMessage(runs.error, "Could not load the runs.")}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table mb-0 leave-table">
                <thead className="custom-table-header">
                  <tr>
                    <th className="ps-4 py-3">Run date</th>
                    <th className="py-3">Trigger</th>
                    <th className="py-3">Finished</th>
                    <th className="py-3 text-end">Created</th>
                    <th className="py-3 text-end">Closed</th>
                    <th className="py-3 text-end">Skipped</th>
                    <th className="py-3 text-end">Errors</th>
                    <th className="py-3 pe-4 text-end">Details</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {list.map((r) => {
                    const isOpen = expanded === r.id;
                    return (
                      <React.Fragment key={r.id}>
                        <tr>
                          <td className="ps-4 fw-semibold">
                            {formatPlainDate(r.runDate)}
                          </td>
                          <td>
                            <span
                              className={`status-pill status-pill--${r.trigger === "MANUAL" ? "amber" : "gray"}`}
                            >
                              {r.trigger === "MANUAL" ? "Manual" : "Daily"}
                            </span>
                            {r.dryRun && (
                              <span className="status-pill status-pill--blue ms-1">
                                Dry run
                              </span>
                            )}
                          </td>
                          <td className="text-nowrap">
                            {r.finishedAt ? (
                              formatDateTime(r.finishedAt)
                            ) : (
                              <span className="text-warning-emphasis">
                                Running…
                              </span>
                            )}
                          </td>
                          <td className="text-end tabular-nums fw-semibold">
                            {r.created ?? 0}
                          </td>
                          <td className="text-end tabular-nums">
                            {r.closed ?? 0}
                          </td>
                          <td className="text-end tabular-nums">
                            {(r.skipped ?? 0).toLocaleString()}
                          </td>
                          <td
                            className={`text-end tabular-nums ${r.errors ? "text-danger fw-bold" : ""}`}
                          >
                            {r.errors ?? 0}
                          </td>
                          <td className="pe-4 text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-light border leave-action-btn"
                              onClick={() => setExpanded(isOpen ? null : r.id)}
                              aria-expanded={isOpen}
                            >
                              {isOpen ? "Hide" : "Show"}
                            </button>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={8} className="replenish-details-cell">
                              <RunDetails run={r} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {!runs.isLoading && list.length === 0 && (
                    <tr>
                      <td colSpan={8} className="approval-empty">
                        <i className="bi bi-arrow-repeat"></i>
                        No runs yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {runs.isLoading && (
            <div className="text-center py-5">
              <span
                className="spinner-border text-primary"
                role="status"
                aria-label="Loading"
              ></span>
            </div>
          )}
        </div>
      </div>

      {showRun && (
        <Dialog
          title="Run leave replenish"
          size={result ? "lg" : "md"}
          onClose={() => setShowRun(false)}
          busy={run.isPending}
          footer={
            <>
              <button
                type="button"
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setShowRun(false)}
                disabled={run.isPending}
              >
                Close
              </button>
              <button
                type="button"
                className={`btn profile-btn ${dryRun ? "btn-primary" : "btn-warning"}`}
                onClick={start}
                disabled={run.isPending || !date}
              >
                {run.isPending && (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  ></span>
                )}
                {dryRun ? "Dry run" : "Run for real"}
              </button>
            </>
          }
        >
          {error && (
            <div className="alert alert-danger fs-085 py-2" role="alert">
              {error}
            </div>
          )}
          <div className="row g-3 align-items-end">
            <div className="col-sm-6">
              <label htmlFor="replenish-date" className="profile-label">
                Run as of
              </label>
              <input
                id="replenish-date"
                type="date"
                className="form-control"
                value={date}
                max={today}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="col-sm-6">
              <div className="form-check form-switch">
                <input
                  id="replenish-dry"
                  type="checkbox"
                  className="form-check-input"
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                />
                <label
                  htmlFor="replenish-dry"
                  className="form-check-label fs-085"
                >
                  Dry run (show what would happen, change nothing)
                </label>
              </div>
            </div>
          </div>
          {!dryRun && (
            <p className="fs-080 text-warning-emphasis mt-3 mb-0">
              <i className="bi bi-exclamation-triangle me-1"></i>
              This creates and closes cycles for real. It's safe to repeat:
              cycles that already exist aren't created twice.
            </p>
          )}
          {result && (
            <div className="mt-3">
              <div className="replenish-result">
                <div>
                  <span className="approval-meta-label">Created</span>
                  <strong>{result.created ?? 0}</strong>
                </div>
                <div>
                  <span className="approval-meta-label">Closed</span>
                  <strong>{result.closed ?? 0}</strong>
                </div>
                <div>
                  <span className="approval-meta-label">Skipped</span>
                  <strong>{(result.skipped ?? 0).toLocaleString()}</strong>
                </div>
                <div>
                  <span className="approval-meta-label">Errors</span>
                  <strong className={result.errors ? "text-danger" : ""}>
                    {result.errors ?? 0}
                  </strong>
                </div>
              </div>
              <RunDetails run={{ ...result, runDate: date }} />
            </div>
          )}
        </Dialog>
      )}
    </Layout>
  );
};

export default ReplenishRunsPage;
